import AppComponent from '../base/app-component.ts';
import { apiGet, apiDelete } from '../lib/api-client.ts';
import type { DataTable } from './data-table.ts';

interface IscrizioneRow {
  id: number;
  categoria: string;
  categoriaNome: string | null;
  data_iscrizione: string;
  data_cessazione: string | null;
  attivo: 0 | 1;
  motivo_cessazione: string | null;
  lavoratore: { id: number; cognome: string; nome: string; codiceFiscale: string | null } | null;
  impresa: { id: number; ragioneSociale: string | null } | null;
}

interface ListaResponse {
  iscrizioni: IscrizioneRow[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface FiltriAvanzati {
  categoria?: string;
  attivo?: '0' | '1';
}

const PAGE_SIZE = 30;

/**
 * wc-iscrizioni-altri-settori-lista - Tabella iscrizioni non-EDC.
 *
 * Wrapper sopra <wc-data-table> con paginazione/sort/filtro server-side.
 * Filtri avanzati (categoria, stato attivo) in offcanvas.
 * La trasmissione a Statini e' gestita altrove (/statini/invio).
 */
export class IscrizioniAltriSettoriLista extends AppComponent {
  static override componentName = 'wc-iscrizioni-altri-settori-lista';
  protected override get templateId() { return 'iscrizioni-altri-settori-lista'; }

  private currentPage = 1;
  private currentFilter = '';
  private currentSort: string | null = null;
  private currentDir: 'asc' | 'desc' | null = null;
  private filtriAvanzati: FiltriAvanzati = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;

  protected override afterRender(): void {
    const dt = this.dataTable;
    if (!dt) return;

    this.listen(dt, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page as number;
      void this.load();
    });
    this.listen(dt, 'dt-sort-change', (e: Event) => {
      const d = (e as CustomEvent).detail as { sort: string | null; dir: 'asc' | 'desc' | null };
      this.currentSort = d.sort;
      this.currentDir = d.dir;
      this.currentPage = 1;
      void this.load();
    });
    this.listen(dt, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter as string;
      this.currentPage = 1;
      void this.load();
    });
    this.listen(dt, 'dt-row-action', (e: Event) => {
      const d = (e as CustomEvent).detail as { action: string; row: IscrizioneRow };
      if (d.action === 'elimina') void this.handleElimina(d.row.id);
    });

    this.setupFiltri();
    void this.load();
  }

  private setupFiltri(): void {
    const offEl = this.$('[data-offcanvas="filtri"]');
    if (offEl) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offEl);
    }
    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.raccogliFiltriAvanzati();
        this.offcanvas?.hide();
        this.currentPage = 1;
        void this.load();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      void this.load();
    });
  }

  private raccogliFiltriAvanzati(): void {
    const catEl = this.$('wc-categoria-select[data-filtro-val="categoria"]') as (HTMLElement & { value?: string }) | null;
    const cat = catEl?.value ?? '';
    const att = (this.$('[data-filtro-val="attivo"]') as HTMLSelectElement | null)?.value ?? '';
    const next: FiltriAvanzati = {};
    if (cat) next.categoria = cat;
    if (att === '0' || att === '1') next.attivo = att;
    this.filtriAvanzati = next;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    this.filtriAvanzati = {};
    this.currentFilter = '';
    const searchInput = this.$('[data-dt-filter-input]') as HTMLInputElement | null;
    if (searchInput) searchInput.value = '';
  }

  private updateFiltriCount(): void {
    const count = Object.values(this.filtriAvanzati).filter(Boolean).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  private get dataTable(): DataTable | null {
    return this.$('wc-data-table') as DataTable | null;
  }

  private async load(): Promise<void> {
    const dt = this.dataTable;
    if (!dt) return;
    dt.setLoading();

    const qs = new URLSearchParams();
    qs.set('page', String(this.currentPage));
    qs.set('limit', String(PAGE_SIZE));
    if (this.filtriAvanzati.categoria) qs.set('categoria', this.filtriAvanzati.categoria);
    if (this.filtriAvanzati.attivo) qs.set('attivo', this.filtriAvanzati.attivo);
    if (this.currentFilter && this.currentFilter.length >= 2) qs.set('q', this.currentFilter);
    if (this.currentSort) qs.set('sort', this.currentSort);
    if (this.currentDir) qs.set('sortDir', this.currentDir);

    const r = await apiGet<ListaResponse>(`iscrizioni-altri-settori?${qs.toString()}`);
    this.updateFiltriCount();
    if (!r.ok) {
      dt.setError(r.error);
      return;
    }
    dt.setData(r.data.iscrizioni as unknown as Record<string, unknown>[], {
      page: r.data.page,
      totalPages: r.data.totalPages,
      count: r.data.count,
      pageSize: r.data.pageSize,
    });
  }

  private async handleElimina(id: number): Promise<void> {
    const ok = await Weext.confirm('Eliminare definitivamente l\'iscrizione?', {
      title: 'Conferma eliminazione',
      confirmText: 'Elimina',
      confirmClass: 'btn-danger',
    });
    if (!ok) return;

    const r = await apiDelete(`iscrizioni-altri-settori/${id}`);
    if (!r.ok) {
      Weext.toast('Errore eliminazione', { type: 'danger' });
      return;
    }
    Weext.toast('Iscrizione eliminata', { type: 'success' });
    void this.load();
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
  }
}
